


import { BehaviorSubject, Subject, map, of, switchMap, take, takeUntil, tap } from "rxjs";
import { LocalCollectionStore } from "../classes";
import { RemoteCollectionStore } from "../classes/remote-collection-store";
import { IFilter, ILoadOptions, ISort } from "../types";
import { AllowedKeyType } from "../types/key-types";



export type DataChangedSideffect = 'reloadChanged' | 'reloadAll' | 'reloadChangedRemote'

export interface IRemoteCollectionStoreConfig<TModel extends object, TMapped extends object = TModel, TKeyType extends AllowedKeyType = string> {

  remoteStore: RemoteCollectionStore<TModel, TKeyType>,
  remoteOperations?: boolean,
  useLocalStore?: boolean,
  map?: (item: TModel) => TMapped,
  unMap?: (mappedItem: TMapped) => TModel

}

/**
 * although RemoteCollectionService is not an abstract class strictly due to problems with dependency injection and abstract class,
 * it should never be injected directly or set to providers array directly,
 * instead it should as an abstract class meaning it is only used by extending
 */
export class RemoteCollectionService<TModel extends object, TMapped extends object = TModel, TKeyType extends AllowedKeyType = string> {

  protected destroy = new Subject<void>();

  protected _autoSync?: boolean = false;
  set autoSync(value) {
    this._autoSync = value;
  }
  get autoSync() {
    return this._autoSync
  }

  readonly remoteDataSubject = new BehaviorSubject<TModel[]>([]);

  /**
   * todo filter
   */
  readonly filterSubject = new BehaviorSubject<IFilter>(undefined);


  readonly sortSubject = new BehaviorSubject<ISort<TModel>[]>(undefined);

  readonly pageIdxSubject = new BehaviorSubject<number>(0);
  readonly pageSizeSubject = new BehaviorSubject<number>(20);
  readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly insertingSubject = new BehaviorSubject<boolean>(false);
  readonly updatingSubject = new BehaviorSubject<boolean>(false);
  readonly removingSubject = new BehaviorSubject<boolean>(false);

  readonly localCollectionStore = new LocalCollectionStore<TModel, TMapped, TKeyType>();

  private _remoteStore!: RemoteCollectionStore<TModel, TKeyType>;
  set remoteStore(value: RemoteCollectionStore<TModel, TKeyType>) {
    this._remoteStore = value;
  }

  private _remoteOperations = false;
  private _useLocalStore = false;

  protected _unMap?: (mappedItem: Partial<TMapped>) => Partial<TModel>;

  constructor() { }

  setConfig(config: IRemoteCollectionStoreConfig<TModel, TMapped, TKeyType>) {
    this._remoteStore = config.remoteStore;

    this.localCollectionStore.configure(this._remoteStore.keyField, config.map);

    if (config.useLocalStore !== undefined) {
      this._useLocalStore = config.useLocalStore;

    }
    if (config.remoteOperations !== undefined) {
      this._remoteOperations = config.remoteOperations;
    }

    this.remoteDataSubject
      .pipe(takeUntil(this.destroy))
      .subscribe((remoteData) => {
        this.localCollectionStore.refresh(remoteData);
      })

    this._unMap = config.unMap;
  }


  load(loadOptions?: ILoadOptions<TModel>) {

    if (!this._remoteOperations) {

      this.loadingSubject.next(true)
      let loadObs = this._remoteStore.load(loadOptions).pipe(tap(() => this.loadingSubject.next(false)));

      if (this.autoSync || loadOptions?.autoSync) {
        loadObs = loadObs.pipe(tap(data => this.remoteDataSubject.next(data)))
      }

      return loadObs;

    }

    const filter = this.filterSubject.value;
    const sort = this.sortSubject.value;

    const { skip, take: lTake } = this._getSkipTake();

    this.loadingSubject.next(true)

    loadOptions = {
      ...(loadOptions || {}),
      filter,
      sort,
      skip,
      take: lTake
    }

    const loadObs = this._remoteStore.load(loadOptions).pipe(tap(() => this.loadingSubject.next(false)));

    if (this.autoSync || loadOptions?.autoSync) {
      loadObs.pipe(tap(data => this.remoteDataSubject.next(data)))
    }


    return loadObs;

  }

  private _forceReload() {
    this.load()
      .pipe(take(1), takeUntil(this.destroy))
      .subscribe((data) =>
        this.remoteDataSubject.next(data)
      )
  }

  private readonly _insertSideffect: { [sideffect in Exclude<DataChangedSideffect, 'reloadChanged'>]: (key: TKeyType, insertJSON: TModel) => any } = {
    reloadAll: () => this._forceReload(),
    reloadChangedRemote: (key) => {

      this.byKey(key).pipe(take(1), takeUntil(this.destroy))
        .subscribe((insertJSON) => {

          this.localCollectionStore.addOne(insertJSON);
        })

    }
  }

  insert(insertJSON: Partial<TMapped>, sideffect?: Exclude<DataChangedSideffect, 'reloadChanged'>) {

    this.insertingSubject.next(true);

    const unmappedInsertJSON = this._unMapOne(insertJSON)

    let insertObs = this._remoteStore.insert(unmappedInsertJSON)
      .pipe(tap(() => this.insertingSubject.next(false)))

    if (this.autoSync) {
      insertObs = insertObs.pipe(tap(() => this.load()))
    }

    if (sideffect) {

      return insertObs
        .pipe(
          take(1),
          switchMap((key) =>
            this.byKey(key)
              .pipe(
                map((insertJSON) => ({ key, insertJSON }))
              )
          ),
          tap(({ insertJSON, key }) => {
            this._insertSideffect[sideffect] && this._insertSideffect[sideffect](key, insertJSON)
          }),
          map(({ key }) => key)
        )
    }

    return insertObs;

  }

  private readonly _updateSideffect: { [sideffect in DataChangedSideffect]: (key: TKeyType, updateJSON: TModel) => any } = {
    reloadAll: () => this._forceReload(),
    reloadChanged: (key, updateJSON) => {

      if (!this._useLocalStore || this._remoteOperations) {
        return;
      }

      this.localCollectionStore.updateOne(key, updateJSON)
    },
    reloadChangedRemote: (key) => {

      if (!this._useLocalStore || this._remoteOperations) {
        return;
      }


      this.byKey(key)
        .pipe(take(1), takeUntil(this.destroy))
        .subscribe((updateJSON) => {
          this.localCollectionStore.updateOne(key, updateJSON)
        })

    }
  }

  update(key: TKeyType, updateJSON: Partial<TMapped>, sideffect?: DataChangedSideffect) {
    const prevValue = { ...this.localCollectionStore.find(key) };

    this.updatingSubject.next(true);
    const unmapped = this._unMapOne(updateJSON);

    if (JSON.stringify(unmapped) === JSON.stringify(prevValue)) {

      return of(unmapped);
    }

    let updateObs = this._remoteStore.update(key, unmapped)
      .pipe(tap(() => this.insertingSubject.next(false)))

    if (sideffect) {

      updateObs = updateObs
        .pipe(
          take(1),
          tap(() => {

            const newValue = {
              ...(this._unMapOne(prevValue || {})),
              ...unmapped
            } as TModel

            this._updateSideffect[sideffect] && this._updateSideffect[sideffect](key, newValue)
          })
        )
    }

    return updateObs;
  }

  private readonly _removeSideffect: { [sideffect in DataChangedSideffect]: (key: TKeyType) => any } = {
    reloadAll: () => this._forceReload(),
    reloadChanged: (key) => {

      if (!this._useLocalStore || this._remoteOperations) {
        return;
      }
      const prev = [...this.localCollectionStore.stateSubject.value];
      this.localCollectionStore.removeOne(key)


    },
    reloadChangedRemote: (key) => {

      if (!this._useLocalStore || this._remoteOperations) {
        return;
      }


      this.byKey(key)
        .pipe(take(1), takeUntil(this.destroy))
        .subscribe((item) => {
          if (item) {
            return;
          }
          this.localCollectionStore.removeOne(key)
        })

    }
  }

  remove(key: TKeyType, sideffect?: DataChangedSideffect) {

    this.removingSubject.next(true);

    let removeObs = this._remoteStore.remove(key)
      .pipe(tap(() => this.removingSubject.next(false)))


    if (sideffect) {

      removeObs = removeObs.pipe(
        take(1),
        tap(() => {
          this._removeSideffect[sideffect] && this._removeSideffect[sideffect](key);

        })
      )

    }

    return removeObs;
  }

  byKey(key: TKeyType) {

    return this._remoteStore.byKey(key);
  }


  private readonly _insertBatchSideffect: { [sideffect in Exclude<DataChangedSideffect, 'reloadChanged'>]: (insertedJSONArray: TModel[]) => any } = {
    reloadAll: () => this._forceReload(),
    reloadChangedRemote: (insertedJSONArray) => {
      this.localCollectionStore.addMany(insertedJSONArray);
    }
  }

  insertBatch(insertJSONArray: TMapped[], sideffect?: Exclude<DataChangedSideffect, 'reloadChanged'>) {

    const unmapped = this._unMapMany(insertJSONArray);
    let insertBatchObs = this._remoteStore.insertBatch(unmapped);


    if (sideffect) {

      insertBatchObs = insertBatchObs
        .pipe(
          take(1),
          tap((inserted) => {
            this._insertBatchSideffect[sideffect] && this._insertBatchSideffect[sideffect](inserted);
          })
        )
    }

    return insertBatchObs;
  }

  private readonly _updateBatchSideffect: { [sideffect in Exclude<DataChangedSideffect, 'reloadChanged'>]: (updated: TModel[]) => any } = {
    reloadAll: () => this._forceReload(),
    reloadChangedRemote: (updated) => {
      this.localCollectionStore.addMany(updated);
    }
  }

  updateBatch(updatePairs: { key: TKeyType, updateJSON: TMapped }[], sideffect?: Exclude<DataChangedSideffect, 'reloadChanged'>) {

    const unmapped = updatePairs.map((updatePair) => ({ ...updatePair, updateJSON: <TModel>this._unMapOne(updatePair.updateJSON) }))
    let updateBatchObs = this._remoteStore.updateBatch(unmapped)

    if (sideffect) {
      updateBatchObs = updateBatchObs.pipe(
        take(1),
        tap((updated) => {
          this._updateBatchSideffect[sideffect] && this._updateBatchSideffect[sideffect](updated);
        })
      )
    }

    return updateBatchObs
  }



  private readonly _removeBatchSideffect: { [sideffect in Exclude<DataChangedSideffect, 'reloadChangedRemote'>]: (keys: TKeyType[]) => any } = {

    reloadAll: () => this._forceReload(),
    reloadChanged: (keys: TKeyType[]) => {
      this.localCollectionStore.removeMany(keys);
    }

  }
  //todo add count
  removeBatch(keys: TKeyType[], sideffect?: Exclude<DataChangedSideffect, 'reloadChangedRemote'>) {

    let removeBatchObs = this._remoteStore.removeBatch(keys);

    if (sideffect) {

      removeBatchObs = removeBatchObs.pipe(
        take(1),
        tap(() => {
          this._removeBatchSideffect[sideffect] && this._removeBatchSideffect[sideffect](keys)

        })
      )

    }

    return removeBatchObs;
  }


  reorderShallow(reorderPairs: { key: TKeyType, orderIdx: number }[]) {

    const reorderObs = this._remoteStore.reorderShallow(reorderPairs);

    return reorderObs;

  }

  private _getSkipTake() {

    const pageIdx = this.pageIdxSubject.value;
    const pageSize = this.pageSizeSubject.value;

    return {
      skip: pageIdx * pageSize,
      take: pageSize
    }
  }

  private _unMapOne(mappedItem: Partial<TMapped>) {
    return this._unMap ? this._unMap(mappedItem) : mappedItem as unknown as TModel;
  }

  private _unMapMany(mappedItems: TMapped[]) {

    return this._unMap ? mappedItems.map(this._unMap) : mappedItems as unknown as TModel[]
  }


  dispose() {

    this.destroy.next();

    this.destroy.complete();
    this.remoteDataSubject.complete();
    this.filterSubject.complete();
    this.sortSubject.complete();
    this.pageIdxSubject.complete();
    this.pageSizeSubject.complete();
    this.loadingSubject.complete();
    this.insertingSubject.complete();
    this.updatingSubject.complete();
    this.removingSubject.complete();

  }


}

